import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, BookOpen } from "lucide-react";
import { GUIDES_BY_SLUG, type Guide } from "@/data/guides";

const CTA_PLAN: Record<"full" | "refusal" | "basic", { to: string; label: string; sub: string }> = {
  full: { to: "/full-support", label: "See Full Support plan", sub: "Backed by The WeLivdIt Method - 4 mocks, DS-160 review, community." },
  refusal: { to: "/refusal-support", label: "See Refusal Support plan", sub: "Backed by The WeLivdIt Method - built for 214(b) re-attempts." },
  basic: { to: "/basic-support", label: "See Basic Support plan", sub: "Solid starting point for first-time interviews." },
};

export const Route = createFileRoute("/resources/$slug")({
  beforeLoad: ({ params }) => {
    if (!GUIDES_BY_SLUG[params.slug]) throw notFound();
  },
  loader: ({ params }) => GUIDES_BY_SLUG[params.slug],
  notFoundComponent: () => (
    <div className="mx-auto max-w-3xl px-5 py-24 text-center">
      <h1 className="font-display text-3xl font-semibold">Guide not found</h1>
      <p className="mt-3 text-foreground/70">The guide you're looking for doesn't exist.</p>
      <Link to="/resources" className="mt-6 inline-block text-primary underline">Browse all guides</Link>
    </div>
  ),
  errorComponent: ({ reset }) => (
    <div className="mx-auto max-w-3xl px-5 py-24 text-center">
      <h1 className="font-display text-3xl font-semibold">Something went wrong</h1>
      <button onClick={reset} className="mt-6 inline-block text-primary underline">Try again</button>
    </div>
  ),
  head: ({ params }) => {
    const g = GUIDES_BY_SLUG[params.slug];
    if (!g) return {};
    return {
      meta: [
        { title: g.metaTitle },
        { name: "description", content: g.description },
        { property: "og:title", content: g.metaTitle },
        { property: "og:description", content: g.description },
        { property: "og:type", content: "article" },
        { property: "og:url", content: `https://welivdit.com/resources/${g.slug}` },
        { property: "og:image", content: "https://welivdit.com/og-share-v6.jpg" },
        { property: "og:image:width", content: "1200" },
        { property: "og:image:height", content: "630" },
        { name: "twitter:image", content: "https://welivdit.com/og-share-v6.jpg" },
      ],
      links: [{ rel: "canonical", href: `https://welivdit.com/resources/${g.slug}` }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Article",
            headline: g.title,
            description: g.description,
            author: { "@type": "Organization", name: "WeLivdIt" },
            publisher: { "@type": "Organization", name: "WeLivdIt", url: "https://welivdit.com" },
            mainEntityOfPage: `https://welivdit.com/resources/${g.slug}`,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: g.faqs.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Home", item: "https://welivdit.com/" },
              { "@type": "ListItem", position: 2, name: "Resources", item: "https://welivdit.com/resources" },
              { "@type": "ListItem", position: 3, name: g.title, item: `https://welivdit.com/resources/${g.slug}` },
            ],
          }),
        },
      ],
    };
  },
  component: GuidePage,
});

function GuidePage() {
  const g = Route.useLoaderData() as Guide;
  const cta = CTA_PLAN[g.ctaPlan];

  return (
    <article className="bg-background">
      <div className="mx-auto max-w-3xl px-5 pt-16 pb-6">
        <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-accent/40 border border-border text-xs">
          <BookOpen className="h-3.5 w-3.5 text-primary" />
          <span className="text-foreground/70 uppercase tracking-wider">Guide</span>
        </div>
        <h1 className="mt-4 font-display text-3xl sm:text-4xl md:text-5xl font-semibold tracking-tight text-balance leading-[1.1]">
          {g.title}
        </h1>
        <p className="mt-4 text-base md:text-lg text-foreground/70 leading-relaxed">{g.description}</p>
      </div>

      <div className="mx-auto max-w-3xl px-5">
        <nav className="rounded-2xl border border-border bg-card p-5">
          <p className="text-xs font-semibold uppercase tracking-wider text-foreground/60">Table of contents</p>
          <ol className="mt-3 space-y-1.5 text-sm">
            {g.toc.map((s, i) => (
              <li key={s.id}>
                <a href={`#${s.id}`} className="text-primary hover:underline">
                  {i + 1}. {s.heading}
                </a>
              </li>
            ))}
            <li>
              <a href="#faq" className="text-primary hover:underline">
                {g.toc.length + 1}. FAQ
              </a>
            </li>
          </ol>
        </nav>
      </div>


      <div className="mx-auto max-w-3xl px-5 py-10 prose-content">
        {g.toc.map((section) => (
          <section key={section.id} id={section.id} className="scroll-mt-20 mt-10 first:mt-0">
            <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">{section.heading}</h2>
            <div className="mt-4 space-y-4 text-foreground/80 leading-relaxed">
              {section.body.map((p, i) => (
                <p key={i}>{p}</p>
              ))}
            </div>
          </section>
        ))}

        <section id="faq" className="scroll-mt-20 mt-14 border-t border-border pt-10">
          <h2 className="font-display text-2xl md:text-3xl font-semibold tracking-tight">Frequently asked questions</h2>
          <div className="mt-6 space-y-3">
            {g.faqs.map((f) => (
              <details key={f.q} className="rounded-2xl border border-border bg-card p-5 group">
                <summary className="cursor-pointer font-medium text-foreground list-none flex justify-between gap-4">
                  <span>{f.q}</span>
                  <span className="text-primary group-open:rotate-45 transition-transform">+</span>
                </summary>
                <p className="mt-3 text-sm text-foreground/75 leading-relaxed">{f.a}</p>
              </details>
            ))}
          </div>
        </section>

        <div className="mt-14 rounded-3xl bg-gradient-to-br from-primary/10 via-sky-50 to-indigo-50 border border-primary/30 p-6 md:p-8">
          <p className="text-xs font-semibold uppercase tracking-wider text-primary">Ready to prepare?</p>
          <h3 className="mt-2 font-display text-xl md:text-2xl font-semibold">{cta.sub}</h3>
          <Link to={cta.to} className="mt-5 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-gradient-cta text-primary-foreground font-semibold hover:opacity-90 transition">
            {cta.label} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>
    </article>
  );
}
